/**
 * Un-draw / re-draw timelines (11 §3) as pure functions of elapsed ms. The
 * renderer applies a `LayerStyle` to an `AthleteDrawing`; nothing here touches
 * a canvas, so the stage table is unit-testable by 14 ID.
 */
import { FX_DISC_REDRAW_MS, FX_DISC_UNDRAW_MS, FX_RESCIND_MAX_MS, FX_TICK_ALPHA_IDLE, FX_UNDRAW_MS } from './constants.js'

export interface LayerState {
  /** Stroke alpha 0..1 (layer visibility). */
  alpha: number
  /** 1 = full path, 0 = erased; erasing end→start. */
  strokeProgress: number
  /** Fraction of the layer's fill that is present, measured from the feet up (1 = full). */
  fillFrac: number
}

export interface LayerStyle {
  layers: LayerState[] // 11 entries, index = layer
  /** Extra alpha for the `1.80` height tick (holds 40 ms longer in S5). */
  heightTickAlpha: number
  /** Pen jitter amplitude in px (S1), 0 under reduced motion. */
  jitterPx: number
  /** Shadow alpha multiplier (fades in lockstep with the fill drain). */
  shadowAlpha: number
  done: boolean
  /** Overall 0..1 progress for rescind bookkeeping. */
  progress: number
}

/** 11 §3.2 S3 slot order: reverse draw order, layer 10 first. */
export const UNDRAW_SLOT_ORDER: readonly number[] = [10, 8, 7, 6, 5, 4, 3, 2]
const SLOT_MS = 40
const S3_START = 160

const clamp01 = (v: number): number => Math.max(0, Math.min(1, v))
const ramp = (t: number, a: number, b: number): number => clamp01((t - a) / (b - a))

function blank(): LayerState[] { return Array.from({ length: 11 }, () => ({ alpha: 1, strokeProgress: 1, fillFrac: 1 })) }

export function normalStyle(): LayerStyle {
  const layers = blank()
  layers[0].alpha = FX_TICK_ALPHA_IDLE
  layers[1].alpha = 0
  return { layers, heightTickAlpha: FX_TICK_ALPHA_IDLE, jitterPx: 0, shadowAlpha: 1, done: false, progress: 0 }
}

/** Hub invuln: construction lines only, no fills (11 §3.3 / 02 §6). */
export function outlineOnlyStyle(): LayerStyle {
  const s = normalStyle()
  for (const l of s.layers) l.fillFrac = 0
  s.layers[1].alpha = 1
  s.shadowAlpha = 0.5
  return s
}

/** Invuln end: fills pour in over 120 ms (top→bottom like re-draw). */
export function invulnEndStyle(tMs: number): LayerStyle {
  const s = normalStyle()
  const f = ramp(tMs, 0, 120)
  for (const l of s.layers) l.fillFrac = f
  s.layers[1].alpha = 1 - f
  s.shadowAlpha = 0.5 + 0.5 * f
  s.done = tMs >= 120
  s.progress = f
  return s
}

/** 11 §3.2 stage table. `tMs` from T0 = hitstop release. */
export function undrawStyle(tMs: number, reducedMotion = false): LayerStyle {
  const t = Math.max(0, tMs)
  const layers = blank()
  // S1 construction reveal 0–80.
  const s1 = ramp(t, 0, 80)
  layers[1].alpha = s1
  layers[0].alpha = FX_TICK_ALPHA_IDLE + (1 - FX_TICK_ALPHA_IDLE) * s1
  const jitterPx = reducedMotion ? 0 : 2 * (1 - ramp(t, 40, 80)) * (t > 0 ? 1 : 0)
  // S2 fill drain 40–220 bottom→top; accent (5) drains in 160–220; hatch (9) fades linearly.
  const drain = ramp(t, 40, 220)
  for (const i of [2, 3, 4, 6, 7, 8, 10]) layers[i].fillFrac = 1 - drain
  layers[5].fillFrac = 1 - ramp(t, 160, 220)
  layers[9].alpha = 1 - ramp(t, 40, 220)
  // S3 stroke erase 160–520, one layer per 40 ms slot, end→start.
  UNDRAW_SLOT_ORDER.forEach((layer, slot) => {
    const a = S3_START + slot * SLOT_MS
    layers[layer].strokeProgress = 1 - ramp(t, a, a + SLOT_MS)
  })
  // Hatch strokes vanish with their alpha; disc fill follows the disc stroke.
  layers[9].strokeProgress = layers[9].alpha
  // S4 construction erase 440–560.
  layers[1].strokeProgress = 1 - ramp(t, 440, 560)
  // S5 ticks fade 520–600; height tick holds 40 ms longer (560–640 clamp → visible at 599).
  const tickFade = 1 - ramp(t, 520, 600)
  layers[0].alpha = layers[0].alpha * tickFade
  const heightTickAlpha = (FX_TICK_ALPHA_IDLE + (1 - FX_TICK_ALPHA_IDLE) * s1) * (1 - ramp(t, 560, 600))
  const done = t >= FX_UNDRAW_MS
  return { layers, heightTickAlpha, jitterPx, shadowAlpha: 1 - drain, done, progress: clamp01(t / FX_UNDRAW_MS) }
}

/** 11 §3.3: exact inverse of the un-draw, stretched to `totalMs`. */
export function redrawStyle(tMs: number, totalMs: number): LayerStyle {
  const k = FX_UNDRAW_MS / Math.max(1, totalMs)
  const tt = FX_UNDRAW_MS - Math.max(0, tMs) * k // time on the un-draw clock, running backward
  const s = undrawStyle(tt, true)
  const done = tMs >= totalMs
  if (done) return normalStyle()
  // At the start of a re-draw nothing is visible yet (un-draw's end state).
  return { ...s, done, progress: clamp01(tMs / totalMs) }
}

/** 11 §10 rescind: from the aborted stage, play the re-draw forward at 2×
 *  (capped at FX_RESCIND_MAX_MS). `abortProgress` = un-draw progress 0..1. */
export function rescindStyle(abortProgress: number, tMs: number): LayerStyle {
  const remainingUndrawMs = clamp01(abortProgress) * FX_UNDRAW_MS
  const dur = Math.min(FX_RESCIND_MAX_MS, remainingUndrawMs / 2)
  if (dur <= 0 || tMs >= dur) return normalStyle()
  const f = tMs / dur
  const tt = remainingUndrawMs * (1 - f)
  const s = undrawStyle(tt, true)
  return { ...s, jitterPx: 0, done: false, progress: f }
}

export function rescindDurationMs(abortProgress: number): number {
  return Math.min(FX_RESCIND_MAX_MS, (clamp01(abortProgress) * FX_UNDRAW_MS) / 2)
}

export interface DiscStyle { tickAlpha: number; ringProgress: number; fillAlpha: number; done: boolean }

/** 11 §3.4: dimension tick 0–60, ring stroke 40–180, accent fill 160–250. */
export function discRedrawStyle(tMs: number): DiscStyle {
  const t = Math.max(0, tMs)
  return { tickAlpha: ramp(t, 0, 60) * (1 - ramp(t, 200, 250)), ringProgress: ramp(t, 40, 180), fillAlpha: ramp(t, 160, 250), done: t >= FX_DISC_REDRAW_MS }
}

/** 11 §3.5: ring erases end→start over 200 ms, no tick stage. */
export function discUndrawStyle(tMs: number): DiscStyle {
  const t = Math.max(0, tMs)
  return { tickAlpha: 0, ringProgress: 1 - ramp(t, 0, FX_DISC_UNDRAW_MS), fillAlpha: 1 - ramp(t, 0, FX_DISC_UNDRAW_MS * 0.5), done: t >= FX_DISC_UNDRAW_MS }
}

/** Reduced-motion variant (09 §4): a single 200 ms fade instead of the stage
 *  table. NB 11 §3.2 keeps the un-draw under reduced motion; this is only
 *  used when BOTH reduced motion and flash reduction are on (15 §7). */
export function fadeStyle(tMs: number, totalMs = 200): LayerStyle {
  const s = normalStyle()
  const a = 1 - ramp(tMs, 0, totalMs)
  for (const l of s.layers) { l.alpha *= a; l.fillFrac = a }
  s.heightTickAlpha *= a
  s.shadowAlpha = a
  s.done = tMs >= totalMs
  s.progress = 1 - a
  return s
}

/** Which layers are visible at all (alpha > 0 and stroke or fill present). */
export function visibleLayers(s: LayerStyle): number[] {
  // Layers 0, 1 and 9 have no fills; only their strokes count.
  return s.layers.map((l, i) => (l.alpha > 1e-6 && (l.strokeProgress > 1e-6 || (![0, 1, 9].includes(i) && l.fillFrac > 1e-6)) ? i : -1)).filter((i) => i >= 0)
}
